/**
 * CE Stock-Notice — kürzt Shopify-Error-Messages für PDP/Cart auf knappe Brand-Wording.
 *
 * Shopify-Default-Texte (DE) sind lang ("Die maximale Menge dieses Artikels befindet
 * sich bereits in deinem Warenkorb."). Im Overlay-Slot über dem ATC-Button passen nur
 * 1-2 Zeilen sauber. Wir mappen bekannte Pattern auf kompakte Wordings.
 *
 * MutationObserver watches `.product-form__error-message`-Elemente — sobald der Text
 * geändert wird (Shopify product-form.js `textContent = errorMessage`), patchen wir
 * ihn. Original-Text bleibt im aria-label für Screenreader erhalten.
 */
(function () {
  if (window.ceStockNoticeInit) return;
  window.ceStockNoticeInit = true;

  // Pattern → kurzer Text. Reihenfolge wichtig: erste passende Regel gewinnt.
  const PATTERNS = [
    {
      match: /maximale Menge.*Warenkorb/i,
      short: 'Limit erreicht',
    },
    {
      match: /nur (\d+) Artikel zu (deinem|Ihrem) Warenkorb/i,
      short: (m) => `Nur ${m[1]} verfügbar`,
    },
    {
      match: /aufgrund der Verfügbarkeit.*nur (\d+)/i,
      short: (m) => `Nur ${m[1]} verfügbar`,
    },
    {
      match: /sold out|ausverkauft/i,
      short: 'Ausverkauft',
    },
    {
      match: /not available|nicht verfügbar/i,
      short: 'Nicht verfügbar',
    },
  ];

  function shorten(text) {
    if (!text || text.length < 50) return text;
    for (const { match, short } of PATTERNS) {
      const m = text.match(match);
      if (m) return typeof short === 'function' ? short(m) : short;
    }
    // Fallback: truncate auf 40 Zeichen + Ellipsis
    return text.length > 40 ? text.slice(0, 40).trim() + '…' : text;
  }

  function patchTextNode(el) {
    // Original-Text in aria-label für Screenreader
    const original = el.textContent;
    if (!original || el.dataset.ceShortened === original) return;
    const short = shorten(original);
    if (short === original) {
      el.dataset.ceShortened = original;
      return;
    }
    el.dataset.ceShortened = original;
    el.setAttribute('aria-label', original);
    el.textContent = short;
  }

  // Auto-Dismiss-Timeout für Stock-Notices (Material-Design-Standard für Info-Toasts)
  const AUTO_DISMISS_MS = 5000;
  const FADE_OUT_MS = 350;

  function findWrapper(textEl) {
    return textEl.closest(
      '.ce-stock-notice, .product-form__error-message-wrapper, .cart-item__error'
    );
  }

  function scheduleAutoDismiss(textEl) {
    if (!textEl) return;
    if (textEl._ceDismissTimer) clearTimeout(textEl._ceDismissTimer);
    if (textEl._ceFadeTimer) clearTimeout(textEl._ceFadeTimer);
    if (!textEl.textContent || textEl.textContent.trim().length === 0) return;
    textEl._ceDismissTimer = setTimeout(() => {
      if (!textEl.textContent || textEl.textContent.trim().length === 0) return;
      const wrapper = findWrapper(textEl);
      if (wrapper) {
        // Fade-out via CSS-Klasse, danach Text leeren
        wrapper.classList.add('ce-stock-notice--fading');
        textEl._ceFadeTimer = setTimeout(() => {
          textEl.textContent = '';
          if (textEl.dataset && textEl.dataset.ceShortened) delete textEl.dataset.ceShortened;
          wrapper.classList.remove('ce-stock-notice--fading');
        }, FADE_OUT_MS);
      } else {
        textEl.textContent = '';
        if (textEl.dataset && textEl.dataset.ceShortened) delete textEl.dataset.ceShortened;
      }
    }, AUTO_DISMISS_MS);
  }

  function observeNoticeText(el) {
    if (el.dataset.ceObserved) return;
    el.dataset.ceObserved = '1';
    if (el.textContent) {
      patchTextNode(el);
      scheduleAutoDismiss(el);
    }
    const observer = new MutationObserver(() => {
      // Bei neuem Text Fade-Klasse entfernen (Reset für nächste Show-Hide-Runde)
      const wrapper = findWrapper(el);
      if (wrapper && el.textContent && el.textContent.trim().length > 0) {
        wrapper.classList.remove('ce-stock-notice--fading');
      }
      patchTextNode(el);
      scheduleAutoDismiss(el);
    });
    observer.observe(el, { characterData: true, childList: true, subtree: true });
  }

  function init() {
    // PDP-Error-Text + Cart-Item-Error-Text beobachten
    document.querySelectorAll('.product-form__error-message, .cart-item__error-text').forEach(observeNoticeText);
  }

  // Auto-Reset via Event-Delegation auf document.
  // Quantity-Input sitzt im Dawn-Theme via `form="..."`-Attribut OUTSIDE des
  // <product-form>-Containers — closest('product-form') greift nicht. Wir
  // finden den Wrapper via Form-ID-Lookup oder Fallback section/document.
  function findWrapperFor(triggerEl) {
    if (!triggerEl) return null;
    // 1) Via form-Attribut auflösen
    const formId = triggerEl.getAttribute && triggerEl.getAttribute('form');
    if (formId) {
      const form = document.getElementById(formId);
      if (form) {
        const pf = form.closest('product-form') || form.parentElement;
        const w = pf && pf.querySelector('.product-form__error-message-wrapper');
        if (w) return w;
      }
    }
    // 2) Via section-id-Container
    const section = triggerEl.closest('[data-section], [data-section-id], section');
    if (section) {
      const w = section.querySelector('.product-form__error-message-wrapper');
      if (w) return w;
    }
    // 3) Letzter Fallback: irgendein Wrapper auf der Page (PDPs haben i.d.R. nur 1)
    return document.querySelector('.product-form__error-message-wrapper');
  }

  // Sauberer Reset: Text leeren statt hidden-Attribute zu manipulieren.
  // Race-Conditions mit Shopify product-form.js vermieden — Shopify schreibt
  // bei neuem Error wieder textContent rein, CSS-Rule :has(:empty) versteckt
  // den Wrapper automatisch wenn leer.
  function clearWrapperText(wrapper) {
    if (!wrapper) return;
    const text = wrapper.querySelector('.product-form__error-message, .cart-item__error-text');
    if (text && text.textContent.trim().length > 0) {
      text.textContent = '';
      // data-ce-shortened reset, damit Shorten-Funktion bei nächstem Error neu greift
      if (text.dataset && text.dataset.ceShortened) delete text.dataset.ceShortened;
    }
  }

  function handleQuantityEvent(e) {
    const target = e.target;
    if (!target) return;
    if (target.name === 'quantity' || (target.tagName === 'INPUT' && target.closest('quantity-input'))) {
      clearWrapperText(findWrapperFor(target));
    }
  }

  function handleClick(e) {
    const target = e.target;
    if (!target || !target.closest) return;
    const qtyBtn = target.closest('quantity-input button, .quantity__button, button[name="plus"], button[name="minus"]');
    if (qtyBtn) {
      clearWrapperText(findWrapperFor(qtyBtn));
    }
  }

  document.addEventListener('input', handleQuantityEvent);
  document.addEventListener('change', handleQuantityEvent);
  document.addEventListener('click', handleClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init nach Section-Re-Render (Shopify Customizer / AJAX-Cart-Updates)
  document.addEventListener('shopify:section:load', init);

  // Cart-Updates ersetzen Markup via cart.js getSectionInnerHTML — neue
  // cart-item__error-text-Elemente brauchen den Observer.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(init, 50));
  } else {
    // Fallback wenn pubsub noch nicht geladen
    let tries = 0;
    const wait = setInterval(() => {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(init, 50));
        clearInterval(wait);
      } else if (++tries > 50) {
        clearInterval(wait);
      }
    }, 100);
  }
})();
